import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Hammer, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import { useSettings } from "@/context/SettingsContext";

function Countdown({ until }) {
  const target = useMemo(() => {
    const t = until ? new Date(until).getTime() : NaN;
    return isNaN(t) ? null : t;
  }, [until]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!target) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);

  if (!target) return null;

  const diff = Math.max(0, target - now);
  const secs = Math.floor(diff / 1000);
  const days = Math.floor(secs / 86400);
  const hours = Math.floor((secs % 86400) / 3600);
  const mins = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const pad = (n) => String(n).padStart(2, "0");
  const fmt = new Intl.DateTimeFormat("bg-BG", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(target));

  if (diff === 0) {
    return (
      <div
        className="mt-8 rounded-xl border border-blue-400/30 bg-blue-500/10 px-5 py-3 text-sm font-semibold text-blue-200"
        data-testid="maintenance-countdown-done"
      >
        Връщаме се всеки момент… страницата ще се обнови автоматично.
      </div>
    );
  }

  const Box = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="min-w-[62px] rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-center font-display text-3xl font-extrabold tabular-nums text-white backdrop-blur sm:min-w-[72px] sm:text-4xl">
        {pad(value)}
      </div>
      <div className="mt-1.5 text-[11px] font-semibold uppercase tracking-widest text-slate-400">
        {label}
      </div>
    </div>
  );
  const Sep = () => <span className="pb-6 text-2xl font-bold text-slate-600">:</span>;

  return (
    <div className="mt-9 flex flex-col items-center" data-testid="maintenance-countdown">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-300">
        Връщаме се след
      </div>
      <div className="mt-4 flex items-center gap-2 sm:gap-3">
        {days > 0 && (
          <>
            <Box value={days} label="дни" />
            <Sep />
          </>
        )}
        <Box value={hours} label="часа" />
        <Sep />
        <Box value={mins} label="мин" />
        <Sep />
        <Box value={s} label="сек" />
      </div>
      <div className="mt-4 text-sm text-slate-400">
        Очаквано връщане: <span className="font-semibold text-slate-200">{fmt} ч.</span>
      </div>
    </div>
  );
}

export default function MaintenancePage({ message, preview = false }) {
  const settings = useSettings();
  const msg = message || settings.maintenance_message;

  return (
    <div
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-slate-950 px-6 text-center"
      data-testid="maintenance-page"
    >
      <Seo title="В процес на обновяване" noindex />

      {preview && (
        <div
          className="absolute inset-x-0 top-0 z-20 flex flex-wrap items-center justify-center gap-2 bg-amber-500 px-4 py-1.5 text-xs font-bold text-white"
          data-testid="maintenance-preview-ribbon"
        >
          Преглед — така изглежда сайтът за посетителите в режим на обновяване.
          <Link to="/admin" className="underline underline-offset-2">
            Обратно към администрацията
          </Link>
        </div>
      )}

      <div className="pointer-events-none absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-primary/25 blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-40 -left-24 h-[440px] w-[440px] rounded-full bg-blue-600/20 blur-[130px]" />

      <div className="relative z-10 flex max-w-lg flex-col items-center">
        <div className="flex items-center gap-3">
          <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary font-display text-3xl font-extrabold text-white shadow-lg shadow-primary/40">
            П
          </span>
          <span className="font-display text-2xl font-extrabold tracking-tight text-white">
            Подуяне <span className="text-blue-400">Днес</span>
          </span>
        </div>

        <div className="mt-12 flex h-16 w-16 items-center justify-center rounded-full border border-white/10 bg-white/5 backdrop-blur">
          <Hammer className="h-7 w-7 text-blue-400" />
        </div>

        <h1 className="mt-8 font-display text-3xl font-extrabold leading-tight text-white sm:text-4xl">
          Страницата е в процес на обновяване
        </h1>
        <p className="mt-4 text-base leading-relaxed text-slate-300">{msg}</p>

        <Countdown until={settings.maintenance_until} />

        {settings.contact_email && (
          <a
            href={`mailto:${settings.contact_email}`}
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:border-blue-400 hover:text-white"
            data-testid="maintenance-contact"
          >
            <Mail className="h-4 w-4" />
            {settings.contact_email}
          </a>
        )}

        <Link
          to="/admin/login"
          className="mt-10 text-xs font-medium uppercase tracking-widest text-slate-500 transition-colors hover:text-slate-300"
          data-testid="maintenance-admin-link"
        >
          Вход за редактори
        </Link>
      </div>
    </div>
  );
}
